// ============================================================
// HoverChat Admin — Dashboard Page
// ============================================================

import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { MessageSquare, Users, Building2, Activity } from 'lucide-react';

export function DashboardPage() {
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    api.getDashboard()
      .then(({ dashboard }) => setStats(dashboard))
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  const statCards = [
    { label: 'Total Tenants', value: stats?.total_tenants || 0, icon: Building2, color: 'bg-blue-500' },
    { label: 'Total Conversations', value: stats?.total_conversations || 0, icon: MessageSquare, color: 'bg-green-500' },
    { label: 'Total Messages', value: stats?.total_messages || 0, icon: Activity, color: 'bg-purple-500' },
    { label: 'Active Today', value: stats?.active_today || 0, icon: Users, color: 'bg-orange-500' },
  ];

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Overview of your chatbot platform</p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card) => (
            <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-medium text-gray-500">{card.label}</p>
                <div className={`${card.color} p-2 rounded-lg`}>
                  <card.icon className="h-5 w-5 text-white" />
                </div>
              </div>
              <p className="text-3xl font-bold text-gray-900">
                {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Start</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="/admin/tenants"
            className="block p-4 border border-gray-200 rounded-lg hover:border-primary/30 hover:bg-primary/5 transition-all"
          >
            <Building2 className="h-6 w-6 text-primary mb-2" />
            <h3 className="font-medium text-gray-900">Create Tenant</h3>
            <p className="text-sm text-gray-500 mt-1">Set up a new chatbot for a client</p>
          </a>
          <div className="block p-4 border border-gray-200 rounded-lg">
            <MessageSquare className="h-6 w-6 text-green-600 mb-2" />
            <h3 className="font-medium text-gray-900">Embed Widget</h3>
            <p className="text-sm text-gray-500 mt-1">
              {'<script src="https://chat.hovercam.com/widget.js" data-site-id="YOUR_KEY"></script>'}
            </p>
          </div>
          <div className="block p-4 border border-gray-200 rounded-lg">
            <Activity className="h-6 w-6 text-purple-600 mb-2" />
            <h3 className="font-medium text-gray-900">Monitor</h3>
            <p className="text-sm text-gray-500 mt-1">View conversations and analytics per tenant</p>
          </div>
        </div>
      </div>
    </div>
  );
}
